/**
 * nt-geo-ui.js — UI components for the geo system. Requires assets/nt-geo.js.
 *
 * <nt-geo-fact data-fact="cart-line" data-type="default">fallback text</nt-geo-fact>
 *   Server-rendered child text IS the national fallback (visible with JS off,
 *   reserves height -> zero CLS). If the resolved zone's config carries a
 *   template for this fact ([TIMER]/[DATE]/[ZIP] placeholders), the component
 *   renders it and keeps it live; otherwise the server text stays untouched.
 *   [TIMER] facts tick once per second ONLY while in the viewport
 *   (IntersectionObserver) — never N free-running intervals.
 *
 * <nt-geo-zip variant="chip|inline"></nt-geo-zip>
 *   The visitor-facing ZIP control. "chip" = announcement-bar pill per Owen's
 *   mock (pin icon, SHIP TO eyebrow, "19103 · PA", caret, popover editor).
 *   "inline" = compact "Shipping to 19103 · change" for cart/PDP surfaces.
 *   The resolved ZIP is rendered ONLY client-side (cache safety).
 *
 * State abbreviations come from the static USPS zip3-prefix table below —
 * display cosmetics only, never used for zone resolution.
 */
(function () {
  'use strict';
  if (window.__ntGeoUi) return;
  window.__ntGeoUi = true;

  function whenGeoReady(fn) {
    if (window.NTGeo) { fn(); return; }
    // nt-geo.js is a deferred head script; by DOMContentLoaded it exists.
    document.addEventListener('DOMContentLoaded', function () {
      if (window.NTGeo) fn();
    });
  }

  /* ---------- zone stamp on <html> (zone-conditional CSS hook) ---------- */
  // data-nt-geo-zone="national|tx-metro|..." — lets stylesheets vary by zone
  // (first consumer: the footer 76ers band hides for tx-metro, nt-geo-ui.css).
  // Client-side only: cached HTML never carries the attribute, so zone rules
  // must only ever HIDE zone-inappropriate content, never reveal content the
  // default page hides — no-JS/geo-failure must equal today's page.
  whenGeoReady(function () {
    NTGeo.subscribe(function (state) {
      if (state && state.zone) {
        document.documentElement.setAttribute('data-nt-geo-zone', state.zone);
      }
    });
  });

  /* ---------- order attribution: hidden cart note (Owen 8-19) ---------- */
  // Stamps _nt_geo_src / _nt_geo_zone cart attributes so every order records
  // whether the buyer set a delivery ZIP (typed/GPS vs auto) and their zone
  // at purchase — read by the Ninja Geo app's Conversions card, never by any
  // customer surface (theme renders no cart attributes — audited 8-18 — and
  // Shopify checkout never displays them; underscore prefix guards print
  // templates). SAFETY: /cart/update.js MERGES attributes key-by-key, so
  // this cannot touch other apps' keys; it never CREATES a cart (writes only
  // when Shopify's cart cookie already exists); fire-and-forget (a failed
  // write is an uncounted order, never a shopper-visible error). US-only by
  // construction: the engine doesn't load on non-US markets, so this module
  // never runs there.
  whenGeoReady(function () {
    var inflight = false, written = null;
    function cartExists() { return /(^|;\s*)cart=/.test(document.cookie); }
    function sync() {
      if (inflight || !cartExists()) return;
      var st = NTGeo.get();
      if (!st || !st.zone) return;
      var src = st.source || 'unknown';
      // _nt_geo_prev_zip: what the shopper's last ZIP change replaced (Scott
      // 8-26) — the join key between error-distance and conversion. Absent for
      // shoppers who never changed a ZIP; same merge-safety as the other keys.
      var prev = (typeof NTGeo.prevChange === 'function') ? NTGeo.prevChange() : null;
      var attrs = { _nt_geo_src: src, _nt_geo_zone: st.zone };
      if (prev && prev.zip) {
        attrs._nt_geo_prev_zip = prev.zip;
        attrs._nt_geo_prev_src = prev.src;
      }
      var val = src + '|' + st.zone + '|' + (prev ? prev.zip : '');
      if (written === val) return;
      inflight = true;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).then(function (r) {
        if (r.ok) written = val;
      }).catch(function () { /* fire-and-forget */ }).then(function () {
        inflight = false;
      });
    }
    NTGeo.subscribe(function () { sync(); });
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) sync();
    });
    // A cart born after load (first add-to-cart on this page) still gets
    // stamped: watch the cookie cheaply until it exists, then stop. Any
    // later page load (cart page, checkout nav) re-syncs via the replay.
    if (!cartExists()) {
      var tries = 0;
      var iv = setInterval(function () {
        if (cartExists() || ++tries > 450) { clearInterval(iv); sync(); }
      }, 4000);
    }
  });

  /* ---------- zip3 -> state (USPS prefix ranges, display-only) ---------- */
  var STATE_RANGES = [
    [5, 5, 'NY'], [6, 7, 'PR'], [8, 8, 'VI'], [9, 9, 'PR'],
    [10, 27, 'MA'], [28, 29, 'RI'], [30, 38, 'NH'], [39, 49, 'ME'],
    [50, 59, 'VT'], [60, 69, 'CT'], [70, 89, 'NJ'],
    [100, 149, 'NY'], [150, 196, 'PA'], [197, 199, 'DE'],
    [200, 200, 'DC'], [201, 201, 'VA'], [202, 205, 'DC'], [206, 219, 'MD'],
    [220, 246, 'VA'], [247, 268, 'WV'], [270, 289, 'NC'], [290, 299, 'SC'],
    [300, 319, 'GA'], [320, 342, 'FL'], [344, 344, 'FL'], [346, 347, 'FL'], [349, 349, 'FL'],
    [350, 369, 'AL'], [370, 385, 'TN'], [386, 397, 'MS'], [398, 399, 'GA'],
    [400, 427, 'KY'], [430, 459, 'OH'], [460, 479, 'IN'], [480, 499, 'MI'],
    [500, 528, 'IA'], [530, 549, 'WI'], [550, 567, 'MN'], [570, 577, 'SD'],
    [580, 588, 'ND'], [590, 599, 'MT'], [600, 629, 'IL'], [630, 658, 'MO'],
    [660, 679, 'KS'], [680, 693, 'NE'], [700, 714, 'LA'], [716, 729, 'AR'],
    [730, 732, 'OK'], [733, 733, 'TX'], [734, 749, 'OK'], [750, 799, 'TX'],
    [800, 816, 'CO'], [820, 831, 'WY'], [832, 838, 'ID'], [840, 847, 'UT'],
    [850, 865, 'AZ'], [870, 884, 'NM'], [885, 885, 'TX'], [889, 898, 'NV'],
    [900, 961, 'CA'], [967, 968, 'HI'], [969, 969, 'GU'], [970, 979, 'OR'],
    [980, 994, 'WA'], [995, 999, 'AK']
  ];
  function stateForZip(zip) {
    if (!zip) return null;
    var p = parseInt(zip.slice(0, 3), 10);
    for (var i = 0; i < STATE_RANGES.length; i++) {
      if (p >= STATE_RANGES[i][0] && p <= STATE_RANGES[i][1]) return STATE_RANGES[i][2];
    }
    return null;
  }

  function fmtRemaining(ms) {
    if (ms <= 0) return '0h 0m';
    var mins = Math.floor(ms / 60000);
    return Math.floor(mins / 60) + 'h ' + (mins % 60) + 'm';
  }

  /* ------------------------------------------------------------------ *
   * <nt-geo-fact>
   * ------------------------------------------------------------------ */
  if (typeof customElements.get('nt-geo-fact') === 'undefined') {
    var factTicker = { els: [], interval: null, io: null };
    function tickVisibleFacts() {
      for (var i = 0; i < factTicker.els.length; i++) {
        var el = factTicker.els[i];
        if (el.__ntVisible) el.__ntRender();
      }
    }
    function watchFact(el) {
      if (factTicker.els.indexOf(el) === -1) factTicker.els.push(el);
      if (!factTicker.io && typeof IntersectionObserver !== 'undefined') {
        factTicker.io = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            entries[i].target.__ntVisible = entries[i].isIntersecting;
          }
        }, { root: null, threshold: 0 });
      }
      if (factTicker.io) factTicker.io.observe(el); else el.__ntVisible = true;
      // ONE shared interval for every [TIMER] fact on the page.
      if (!factTicker.interval) factTicker.interval = setInterval(tickVisibleFacts, 1000);
    }
    function unwatchFact(el) {
      var i = factTicker.els.indexOf(el);
      if (i > -1) factTicker.els.splice(i, 1);
      if (factTicker.io) factTicker.io.unobserve(el);
    }

    class NtGeoFact extends HTMLElement {
      connectedCallback() {
        var el = this;
        el.__ntFallback = el.textContent;
        whenGeoReady(function () {
          el.__ntRender = function () {
            var fact = el.getAttribute('data-fact') || 'cart-line';
            var type = el.getAttribute('data-type') || 'default';

            // Offer-aware facts: when the element carries a cart total and
            // the zone has tiers, pick the "-max" template variant once the
            // top tier is reached ('cart-offer' vs 'cart-offer-max').
            var cartAttr = el.getAttribute('data-cart-total');
            var offer = null;
            if (cartAttr !== null && window.NTGeo.offerState) {
              offer = NTGeo.offerState(parseInt(cartAttr, 10) || 0);
            }
            var template = (offer && !offer.next && NTGeo.templateFor(fact + '-max'))
              || NTGeo.templateFor(fact);
            if (!template) { el.textContent = el.__ntFallback; return; }
            var out = template;

            // '[DATE' (no closing bracket) so [DATE:method] templates enter
            // the substitution block too — a template with ONLY [DATE:ground]
            // painted its raw placeholder (caught on the v4 PDP, 8-13).
            if (out.indexOf('[TIMER]') > -1 || out.indexOf('[DATE') > -1) {
              var s = NTGeo.cutoffFor(type);
              out = out.split('[TIMER]').join(fmtRemaining(s.msRemaining));
              // [DATE:method] (tier transit) before bare [DATE] (legacy matrix)
              out = out.replace(/\[DATE:([a-z0-9-]+)\]/g, function (_, method) {
                var d = NTGeo.deliveryFor(method, type);
                return d ? d.label : s.deliveryLabel;
              });
              out = out.split('[DATE]').join(s.deliveryLabel);
            }
            if (offer) {
              out = out.split('[OFFER]').join(offer.achieved ? offer.achieved.label : '');
              if (offer.next) {
                out = out.split('[NEXT_AMOUNT]').join('$' + (offer.remainingCents / 100).toFixed(2).replace(/\.00$/, ''));
                out = out.split('[NEXT_LABEL]').join(offer.next.label);
                var nd = NTGeo.deliveryFor(offer.next.method, type);
                out = out.split('[NEXT_DATE]').join(nd ? nd.label : '');
              }
            }
            var st = NTGeo.get();
            out = out.split('[ZIP]').join((st && st.zip) || '');
            if (el.textContent !== out) el.textContent = out;
            if (template.indexOf('[TIMER]') > -1 && !el.__ntWatched) {
              el.__ntWatched = true;
              watchFact(el);
            }
          };
          el.__ntUnsub = NTGeo.subscribe(function () { el.__ntRender(); });
        });
      }
      disconnectedCallback() {
        if (this.__ntUnsub) this.__ntUnsub();
        unwatchFact(this);
      }
    }
    customElements.define('nt-geo-fact', NtGeoFact);
  }

  /* ------------------------------------------------------------------ *
   * <nt-geo-tiers> — the launch-offer progress card (Owen's mock 8-11):
   * segmented bar with a marker per tier ($75 2nd Day | $99 Next Day),
   * "Add $X for FREE ..." pointing at the next tier, achieved tiers get a
   * green check + green label. Reads the BUCKET total (Transfers &
   * Supplies) from data-cart-total — same source as the legacy fs-card it
   * replaces. Cache safety: the legacy card stays the server-rendered
   * default; when the resolved zone carries offers this component unhides
   * itself and hides the legacy sibling — all client-side.
   * ------------------------------------------------------------------ */
  if (typeof customElements.get('nt-geo-tiers') === 'undefined') {
    var CHECK_SVG = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="8" cy="8" r="8" fill="#22a04b"/><path d="M4.6 8.3l2.2 2.2 4.6-4.8" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';

    function shortLabel(offer) {
      if (offer.short) return offer.short;
      // "Free 2nd Day Delivery" -> "2nd Day"
      return String(offer.label || '').replace(/^free\s+/i, '').replace(/\s+(delivery|shipping)$/i, '');
    }

    class NtGeoTiers extends HTMLElement {
      connectedCallback() {
        var el = this;
        whenGeoReady(function () {
          el.__ntUnsub = NTGeo.subscribe(function () { el.__ntRender(); });
          el.__ntRender();
        });
      }

      __legacy() {
        var p = this.parentElement;
        return p ? p.querySelector('.fs-card--bysize:not(.ntgt)') : null;
      }

      __ntRender() {
        var el = this;
        var st = NTGeo.get();
        var z = st && st.zoneCfg;
        var offers = (z && Array.isArray(z.offers)) ? z.offers.slice().sort(function (a, b) { return a.min - b.min; }) : [];
        var legacy = el.__legacy();

        if (!offers.length) {
          el.hidden = true;
          if (legacy) legacy.style.display = '';
          return;
        }
        if (legacy) legacy.style.display = 'none';
        el.hidden = false;

        var total = parseInt(el.getAttribute('data-cart-total'), 10) || 0;
        var s = NTGeo.offerState(total);

        // ---- static scaffold once ----
        if (!el.__built) {
          el.__built = true;
          el.innerHTML =
            '<span class="fs-card__label"></span>' +
            '<span class="ntgt__unlocked" hidden>' + CHECK_SVG + '<b class="ntgt__unlocked-text"></b></span>' +
            '<span class="ntgt__add" hidden>Add <b class="ntgt__amount"></b> for <b class="ntgt__goal"></b></span>' +
            '<span class="ntgt__bar"></span>' +
            '<span class="ntgt__marks"></span>';
          el.querySelector('.fs-card__label').textContent = el.getAttribute('data-bucket-label') || 'Transfers & Supplies';
        }

        // ---- status lines ----
        var unlocked = el.querySelector('.ntgt__unlocked');
        var add = el.querySelector('.ntgt__add');
        if (s.achieved) {
          unlocked.hidden = false;
          el.querySelector('.ntgt__unlocked-text').textContent = s.achieved.label.replace(/^free\s+/i, '') + ' Unlocked';
        } else {
          unlocked.hidden = true;
        }
        if (s.next) {
          add.hidden = false;
          el.querySelector('.ntgt__amount').textContent = '$' + (s.remainingCents / 100).toFixed(2);
          el.querySelector('.ntgt__goal').textContent = s.next.label;
        } else {
          add.hidden = true;
        }

        // ---- segmented bar + labels (rebuilt per render: tier count is tiny) ----
        var bar = el.querySelector('.ntgt__bar');
        var marks = el.querySelector('.ntgt__marks');
        bar.textContent = ''; marks.textContent = '';
        var prev = 0;
        offers.forEach(function (o) {
          var spanCents = (o.min - prev) * 100;
          var fillCents = Math.max(0, Math.min(total - prev * 100, spanCents));
          var seg = document.createElement('span');
          seg.className = 'ntgt__seg';
          seg.style.flexGrow = String(o.min - prev);
          var fill = document.createElement('span');
          fill.className = 'ntgt__seg-fill';
          fill.style.width = (spanCents ? (fillCents / spanCents) * 100 : 0) + '%';
          seg.appendChild(fill);
          bar.appendChild(seg);

          var mark = document.createElement('span');
          mark.className = 'ntgt__mark' + (total >= o.min * 100 ? ' ntgt__mark--hit' : '');
          mark.style.flexGrow = String(o.min - prev);
          var amt = document.createElement('b');
          amt.textContent = '$' + o.min;
          mark.appendChild(amt);
          mark.appendChild(document.createTextNode(' ' + shortLabel(o)));
          marks.appendChild(mark);
          prev = o.min;
        });
      }

      disconnectedCallback() { if (this.__ntUnsub) this.__ntUnsub(); }
    }
    customElements.define('nt-geo-tiers', NtGeoTiers);
  }

  /* ------------------------------------------------------------------ *
   * <nt-geo-zip>
   * ------------------------------------------------------------------ */
  if (typeof customElements.get('nt-geo-zip') === 'undefined') {
    // Attribute-styled SVGs only — inline <style> in SVG leaks document-wide.
    var PIN_SVG = '<svg class="nt-geoz__pin" width="14" height="16" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6 0C2.9 0 .5 2.4.5 5.4c0 3.8 4.9 8.2 5.1 8.4a.6.6 0 0 0 .8 0c.2-.2 5.1-4.6 5.1-8.4C11.5 2.4 9.1 0 6 0Zm0 7.6a2.2 2.2 0 1 1 0-4.4 2.2 2.2 0 0 1 0 4.4Z" fill="#019aff"/></svg>';
    var CARET_SVG = '<svg class="nt-geoz__caret" width="9" height="6" viewBox="0 0 9 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1l3.5 3.5L8 1" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';

    class NtGeoZip extends HTMLElement {
      connectedCallback() {
        var el = this;
        if (el.__ntBuilt) return;
        el.__ntBuilt = true;
        var variant = el.getAttribute('variant') || 'inline';

        if (variant === 'chip') {
          // One line per Owen's 8-12 mock: pin · "Ship to:" · bold ZIP · caret
          // (no eyebrow stack, no state abbreviation in the chip).
          el.innerHTML =
            '<button type="button" class="nt-geoz__btn" aria-haspopup="dialog" aria-expanded="false">' +
              PIN_SVG +
              '<span class="nt-geoz__label">Ship to:</span>' +
              '<span class="nt-geoz__val" data-nt-zipval>&mdash;</span>' +
              CARET_SVG +
            '</button>' +
            el.__popoverHTML();
        } else {
          el.innerHTML =
            '<button type="button" class="nt-geoz__btn nt-geoz__btn--inline" aria-haspopup="dialog" aria-expanded="false">' +
              PIN_SVG +
              '<span class="nt-geoz__inline-label">Shipping to&nbsp;</span>' +
              '<span class="nt-geoz__val" data-nt-zipval>&mdash;</span>' +
              '<span class="nt-geoz__change">change</span>' +
            '</button>' +
            el.__popoverHTML();
        }

        el.__btn = el.querySelector('.nt-geoz__btn');
        el.__pop = el.querySelector('.nt-geoz__pop');
        el.__input = el.querySelector('.nt-geoz__input');
        el.__err = el.querySelector('.nt-geoz__err');
        el.__val = el.querySelector('[data-nt-zipval]');

        el.__btn.addEventListener('click', function (e) { e.preventDefault(); el.__toggle(); });
        el.__save = el.querySelector('.nt-geoz__save');
        el.__save.addEventListener('click', function () { el.__submit(); });
        el.querySelector('.nt-geoz__x').addEventListener('click', function () { el.__close(); });
        el.__input.addEventListener('input', function () {
          el.__save.disabled = !/^\d{5}$/.test(el.__input.value.trim());
        });
        el.__gps = el.querySelector('.nt-geoz__gps');
        el.__gps.addEventListener('click', function () {
          // Contextual permission ask (Owen's call): the browser prompt only
          // ever appears from this explicit click inside the ZIP editor.
          if (!window.NTGeo) return;
          el.__gps.disabled = true;
          el.__gps.textContent = 'Locating…';
          NTGeo.locate().then(function (res) {
            el.__gps.disabled = false;
            el.__gps.innerHTML = ''; el.__gps.insertAdjacentHTML('beforeend', PIN_SVG + 'Use my location');
            if (res.ok) { el.__close(); return; }
            if (res.denied) el.__gps.hidden = true; // remembered — don't nag
            el.__err.textContent = res.error;
            el.__err.hidden = false;
          });
        });
        // Previously declined? Keep the popover typing-only.
        if (window.NTGeo && NTGeo.locationDenied && NTGeo.locationDenied()) el.__gps.hidden = true;
        el.__input.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { e.preventDefault(); el.__submit(); }
          if (e.key === 'Escape') el.__close();
        });
        el.__outside = function (e) { if (!el.contains(e.target)) el.__close(); };

        whenGeoReady(function () {
          el.__ntUnsub = NTGeo.subscribe(function (state) {
            if (state.zip) {
              // chip = bare ZIP (mock); inline keeps "12345 · ST"
              var st = variant === 'chip' ? null : stateForZip(state.zip);
              el.__val.textContent = state.zip + (st ? ' · ' + st : '');
              el.classList.add('nt-geoz--has-zip');
            } else {
              el.__val.textContent = 'Enter ZIP';
              el.classList.remove('nt-geoz--has-zip');
            }
          });
        });
      }

      __popoverHTML() {
        // Owen's comp (8-12): white card w/ top arrow, bold title + X close,
        // full-width input ("Enter Zip Code"), full-width Update (disabled
        // until 5 digits), divider, then the location option. The arrow is a
        // REAL node, never a pseudo-element (Yottaa paint-suppression).
        var X_SVG = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
        return '<span class="nt-geoz__pop" role="dialog" aria-label="Enter your delivery ZIP code" hidden>' +
          '<span class="nt-geoz__arrow" aria-hidden="true"></span>' +
          '<span class="nt-geoz__pop-head">' +
            '<span class="nt-geoz__pop-title">Enter Your US Delivery Zip Code</span>' +
            '<button type="button" class="nt-geoz__x" aria-label="Close">' + X_SVG + '</button>' +
          '</span>' +
          '<input class="nt-geoz__input" type="text" inputmode="numeric" autocomplete="postal-code" maxlength="5" placeholder="Enter Zip Code" aria-label="ZIP code">' +
          '<button type="button" class="nt-geoz__save" disabled>Update</button>' +
          '<span class="nt-geoz__err" role="alert" hidden></span>' +
          '<span class="nt-geoz__divider" aria-hidden="true"></span>' +
          '<button type="button" class="nt-geoz__gps">' + PIN_SVG + 'Use my location</button>' +
        '</span>';
      }

      __toggle() { this.__pop.hidden ? this.__open() : this.__close(); }

      __open() {
        this.__pop.hidden = false;
        this.__btn.setAttribute('aria-expanded', 'true');
        this.__err.hidden = true;
        var st = window.NTGeo && NTGeo.get();
        this.__input.value = (st && st.zip) || '';
        if (this.__save) this.__save.disabled = !/^\d{5}$/.test(this.__input.value.trim());
        // Narrow phones: a 320px card can't hang off a chip that sits mid-
        // header — pin it fixed under the chip, full-ish width.
        if (window.innerWidth < 480) {
          this.__pop.classList.add('nt-geoz__pop--fixed');
          this.__pop.style.top = Math.round(this.__btn.getBoundingClientRect().bottom + 10) + 'px';
        } else {
          this.__pop.classList.remove('nt-geoz__pop--fixed');
          this.__pop.style.top = '';
        }
        var el = this;
        setTimeout(function () { el.__input.focus(); }, 0);
        document.addEventListener('click', this.__outside, true);
        if (window.NTGeo) NTGeo.track('nt_geo_zip_opened', { variant: this.getAttribute('variant') || 'inline' });
      }

      __close() {
        this.__pop.hidden = true;
        this.__btn.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.__outside, true);
      }

      __submit() {
        if (!window.NTGeo) return;
        var res = NTGeo.set(this.__input.value);
        if (res.ok) { this.__close(); return; }
        this.__err.textContent = res.error;
        this.__err.hidden = false;
      }

      disconnectedCallback() {
        if (this.__ntUnsub) this.__ntUnsub();
        document.removeEventListener('click', this.__outside, true);
      }
    }
    customElements.define('nt-geo-zip', NtGeoZip);
  }

  /* ------------------------------------------------------------------ *
   * Zone-targeted variant IMAGE SWAP (Owen 8-17) — MERCHANDISING ONLY.
   * (Replaces the zone-exclusive variant HIDING of 8-14 — Owen's call:
   * never hide options, swap imagery instead.)
   *
   * A zone's config may carry `imageSwaps: { "<variantId>": "<https url>" }`.
   * For shoppers resolved to that zone, on a product that owns a targeted
   * variant:
   *   - on LOAD the featured image swaps (to the selected variant's image
   *     when the selection is targeted, else the product's first targeted
   *     entry) — the zone visual shows regardless of initial selection;
   *   - SELECTING a targeted variant swaps to that variant's image;
   *   - selecting an untargeted variant (or leaving the zone) restores the
   *     original image bytes exactly (src + srcset + sizes kept on the
   *     element in data-nt-geo-orig).
   * Nothing else changes: pickers, builder, cart, checkout all untouched.
   *
   * No Liquid changes: product/variant identity comes from Shopify's own
   * page meta (ShopifyAnalytics.meta.product), selection from the add-to-
   * cart form's [name="id"] or the checked .product-variant__input values
   * matched against variants[].public_title.
   * ------------------------------------------------------------------ */
  (function () {
    function swapTargetImg() {
      // Priority: gang-sheet gallery main image -> active slider slide ->
      // first image inside the generic gallery wrapper.
      return document.querySelector('.grt-main-image img') ||
        document.querySelector('.product-gallery .css-slide-active img') ||
        document.querySelector('[data-js-product-gallery] img');
    }

    function zoneSwaps(state) {
      var cfg = window.NTGeo && NTGeo.config();
      var zone = cfg && cfg.zones && cfg.zones[state.zone];
      var map = zone && zone.imageSwaps;
      if (!map || typeof map !== 'object') return null;
      var clean = {};
      var any = false;
      for (var vid in map) {
        if (/^https:\/\//.test(String(map[vid]))) { clean[String(vid)] = String(map[vid]); any = true; }
      }
      return any ? clean : null;
    }

    function productEntries(swaps) {
      var meta = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || window.meta;
      var product = meta && meta.product;
      if (!product || !Array.isArray(product.variants)) return null;
      var entries = null;
      for (var i = 0; i < product.variants.length; i++) {
        var vid = String(product.variants[i].id);
        if (swaps[vid]) (entries = entries || {})[vid] = swaps[vid];
      }
      return entries; // null when THIS product has no targeted variant
    }

    function selectedVariantId() {
      // The PICKER is the source of truth: join the checked option values and
      // match against public_title. Do NOT prefer form [name=id] — pages like
      // the gang sheet builder carry several /cart/add forms (recommendations,
      // sticky bar) and the first one is routinely a different product, which
      // made strict syncs restore instead of swap (8-17 bug).
      var checked = document.querySelectorAll('.product-variant__input:checked');
      if (checked.length) {
        var vals = [];
        for (var i = 0; i < checked.length; i++) vals.push((checked[i].value || '').trim());
        var joined = vals.join(' / ');
        var meta = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || window.meta;
        var variants = meta && meta.product && meta.product.variants;
        if (variants) {
          for (var j = 0; j < variants.length; j++) {
            if ((variants[j].public_title || '') === joined) return String(variants[j].id);
          }
        }
      }
      var idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
      if (idInput && idInput.value) return String(idInput.value);
      return null;
    }

    // The originals live ON the element (data-nt-geo-orig), never in a JS
    // stash: theme gallery scripts replace/clone the main <img> between our
    // calls, and a detached-node stash restored the wrong bytes (found in
    // the 8-17 sandbox pass). An attribute rides along with any clone and
    // restore always targets the CURRENTLY queried element.
    function applySwap(url) {
      var img = swapTargetImg();
      if (!img) return;
      if (!img.hasAttribute('data-nt-geo-orig')) {
        img.setAttribute('data-nt-geo-orig', JSON.stringify({
          src: img.getAttribute('src'),
          srcset: img.getAttribute('srcset'),
          sizes: img.getAttribute('sizes')
        }));
      }
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = url;
      img.setAttribute('data-nt-geo-imgswap', '');
    }

    function restore() {
      var img = swapTargetImg();
      if (!img || !img.hasAttribute('data-nt-geo-orig')) return;
      var orig;
      try { orig = JSON.parse(img.getAttribute('data-nt-geo-orig')); } catch (e) { orig = null; }
      if (orig) {
        if (orig.srcset !== null) img.setAttribute('srcset', orig.srcset); else img.removeAttribute('srcset');
        if (orig.sizes !== null) img.setAttribute('sizes', orig.sizes); else img.removeAttribute('sizes');
        if (orig.src !== null) img.setAttribute('src', orig.src);
      }
      img.removeAttribute('data-nt-geo-orig');
      img.removeAttribute('data-nt-geo-imgswap');
    }

    function firstEntryUrl(entries) {
      for (var vid in entries) return entries[vid];
      return null;
    }

    var entries = null; // this product's targeted variants for the CURRENT zone
    var desiredUrl = null; // what should be painted right now (null = original)

    // Gallery scripts (sliders, lazyloaders — the gang sheet builder does
    // this seconds after load) replace or rewrite the main <img> AFTER a
    // swap has been applied, silently undoing it. A guarded, debounced
    // observer re-applies the desired state; it no-ops in O(1) when the
    // image already matches, and is only connected while this product
    // actually has a live swap target.
    var mo = null;
    var moTimer = null;
    function enforce() {
      try {
        if (!desiredUrl) return;
        var img = swapTargetImg();
        if (!img) return;
        if (img.getAttribute('src') === desiredUrl && img.hasAttribute('data-nt-geo-imgswap')) return;
        applySwap(desiredUrl);
      } catch (e) { /* never break a PDP */ }
    }
    function watch(on) {
      if (on && !mo && typeof MutationObserver === 'function') {
        mo = new MutationObserver(function () {
          if (moTimer) return;
          moTimer = setTimeout(function () { moTimer = null; enforce(); }, 150);
        });
        mo.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['src', 'srcset']
        });
      } else if (!on && mo) {
        mo.disconnect();
        mo = null;
      }
    }

    // strict=false (zone resolution / page load): the zone image paints
    // regardless of which variant is selected — the product-level default.
    // strict=true (the shopper actively picked a variant): only a TARGETED
    // variant shows its zone image; an untargeted pick RESTORES the real
    // product image (Owen 8-17: never leave the zone visual on a variant
    // the shopper explicitly chose — the original is always the safe frame).
    function sync(strict) {
      try {
        if (!entries) { desiredUrl = null; watch(false); restore(); return; }
        var sel = selectedVariantId();
        var url = (sel && entries[sel]) || (strict ? null : firstEntryUrl(entries));
        desiredUrl = url || null;
        watch(true); // stay connected while a swap COULD apply on this product
        if (url) applySwap(url); else restore();
      } catch (e) { /* merchandising sugar — never break a PDP over it */ }
    }

    var lastState = null;
    var userPicked = false; // once the shopper chose a variant, syncs stay strict
    var metaSeen = false;   // product meta observed at least once

    function metaProduct() {
      var meta = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || window.meta;
      return (meta && meta.product && Array.isArray(meta.product.variants)) ? meta.product : null;
    }

    function recompute() {
      try {
        var product = metaProduct();
        if (!product || !lastState) { entries = null; return false; }
        metaSeen = true;
        var swaps = zoneSwaps(lastState);
        entries = swaps ? productEntries(swaps) : null;
      } catch (e) { entries = null; }
      sync(userPicked);
      return true;
    }

    // BOOT RACE (found 8-17 on build-your-own-gang-sheet, 80 variants):
    // on heavy PDPs BOTH ShopifyAnalytics.meta.product and the gallery <img>
    // can land seconds after the subscribe replay (a fixed 1.5s retry lost
    // this race twice). Retry on a widening ladder until either the product
    // proves irrelevant (meta present, no targeted variant) or a swap has
    // painted; every attempt is a cheap no-op re-sync otherwise.
    var RETRY_MS = [400, 1000, 2000, 4000, 8000];
    function attempt(i) {
      var settled = recompute() && (
        !entries || // meta present and this product has no targeted variant
        (function () { var img = swapTargetImg(); return !!(img && (img.hasAttribute('data-nt-geo-imgswap') || userPicked)); })()
      );
      if (!settled && i < RETRY_MS.length) {
        setTimeout(function () { attempt(i + 1); }, RETRY_MS[i]);
      }
    }

    whenGeoReady(function () {
      NTGeo.subscribe(function (state) {
        lastState = state;
        attempt(0);
      });
    });
    // Selection changes: delegated capture survives picker re-renders. If the
    // boot race left entries uncomputed, recompute lazily before the strict
    // sync so a direct targeted pick always works.
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t) return;
      // ONLY a REAL shopper interaction counts as a pick. The gang sheet
      // builder programmatically re-dispatches change on the picker radios
      // during boot (and price engines sync hidden [name=id] inputs), which
      // flipped strict mode before any human touched the page and killed
      // the load swap (8-17 bug). e.isTrusted is the discriminator: only
      // user-agent-generated events carry it. Programmatic changes still
      // re-sync — at the CURRENT strictness, never escalating it.
      if (t.className && String(t.className).indexOf('product-variant__input') > -1) {
        if (e.isTrusted) {
          userPicked = true;
          if (!metaSeen) recompute(); else sync(true);
        } else if (!metaSeen) {
          recompute();
        } else {
          sync(userPicked);
        }
      }
    }, true);

    // Diagnostics hook (console-only, not a contract).
    window.__ntGeoImgSwap = {
      state: function () {
        return {
          entries: entries, desiredUrl: desiredUrl, metaSeen: metaSeen,
          userPicked: userPicked, zone: lastState && lastState.zone,
          observing: !!mo
        };
      },
      recompute: recompute
    };
  }());

  /* ------------------------------------------------------------------ *
   * Zone home link (Owen 8-17) — for shoppers in a zone with a landing
   * page, the header logo IS their homepage: clicking it lands on the
   * zone page from anywhere. The once-per-session steer stays; typing
   * the bare domain still reaches the real homepage afterwards (escape
   * hatch), and ?noredirect=1 keeps working. Only logo anchors whose
   * href is exactly the site root are adopted — blanks-mode and app-mode
   * logos point elsewhere and are never touched. Original href rides on
   * the element (data-nt-geo-home-orig) for exact restore on zone exit.
   * ------------------------------------------------------------------ */
  (function () {
    function applyHomeLinks(state) {
      try {
        var cfg = window.NTGeo && NTGeo.config();
        var zone = cfg && cfg.zones && cfg.zones[state.zone];
        var landing = (zone && typeof zone.landing === 'string' && /^\/(?!\/)/.test(zone.landing))
          ? zone.landing.split('?')[0] : null;
        var links = document.querySelectorAll('a.logo-img');
        for (var i = 0; i < links.length; i++) {
          var a = links[i];
          var orig = a.getAttribute('data-nt-geo-home-orig');
          if (landing) {
            if (orig === null) {
              var href = a.getAttribute('href') || '';
              if (href === '/' || href === '') {
                a.setAttribute('data-nt-geo-home-orig', href);
                a.setAttribute('href', landing);
              }
            } else {
              a.setAttribute('href', landing);
            }
          } else if (orig !== null) {
            a.setAttribute('href', orig);
            a.removeAttribute('data-nt-geo-home-orig');
          }
        }
      } catch (e) { /* navigation sugar — never break the header */ }
    }
    whenGeoReady(function () { NTGeo.subscribe(applyHomeLinks); });
  }());

  /* ------------------------------------------------------------------ *
   * Zone-change navigation (Owen 8-17, demo feedback) — when the shopper
   * EXPLICITLY changes their ZIP (typed or GPS), re-route between the
   * homepage and zone landing pages:
   *   - on the homepage: new zone has a landing -> go there
   *   - on a zone's landing page: new zone's landing differs -> go there;
   *     new zone has none -> go to the default homepage
   *   - anywhere else (product pages etc.): NEVER navigate — the page's
   *     surfaces just update in place like they already do.
   * Boot replay, late IP upgrades, and deep links never move the page —
   * only a change the shopper makes while on it.
   * ------------------------------------------------------------------ */
  (function () {
    var lastZone = null;
    var booted = false;
    whenGeoReady(function () {
      NTGeo.subscribe(function (state) {
        var prev = lastZone;
        lastZone = state.zone;
        if (!booted) { booted = true; return; } // first callback = replay of current state
        if (state.zone === prev) return;
        if (state.source !== 'manual' && state.source !== 'gps') return;
        try {
          var cfg = NTGeo.config();
          var landings = {};
          for (var zid in (cfg.zones || {})) {
            var l = cfg.zones[zid] && cfg.zones[zid].landing;
            if (typeof l === 'string' && /^\/(?!\/)/.test(l)) landings[zid] = l.split('?')[0];
          }
          var here = location.pathname;
          var newLanding = landings[state.zone] || null;
          var target = null;
          if (here === '/') {
            if (newLanding) target = newLanding;
          } else {
            var onLandingPage = false;
            for (var z in landings) { if (landings[z] === here) { onLandingPage = true; break; } }
            if (onLandingPage) {
              if (newLanding && newLanding !== here) target = newLanding;
              else if (!newLanding) target = '/';
            }
          }
          if (!target) return;
          try { sessionStorage.setItem('ntGeoSteered', '1'); } catch (x) { /* fine */ }
          // assign (not replace): the shopper triggered this — back button
          // should return them to where they were.
          location.assign(target);
        } catch (e) { /* navigation sugar — never break the page */ }
      });
    });
  }());
}());
